import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import ShareRow from "@/components/ShareRow";
import { Reveal, MaskLine, SectionHead } from "@/components/Reveal";
import { IMAGES, PRINCIPLES, ARCHITECTURE } from "@/data/content";

const About = () => (
  <>
    <SEO
      title="About"
      path="/about"
      description="A permanent, non-partisan civic intelligence institution transforming ward-level governance across India through data-driven accountability, citizen participation, public leadership, and technology-enabled urban transformation — built on six years of research and four inviolable principles."
    />
    <section className="relative overflow-hidden pt-28 md:pt-52" data-testid="about-hero">
      <div className="mx-auto max-w-[1600px] px-5 pb-20 md:px-10 md:pb-28">
        <MaskLine delay={0.1}>
          <span className="text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-gold md:text-sm">About Smart Nagar Nigam</span>
        </MaskLine>
        <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight text-ivory">
          <MaskLine delay={0.25} className="text-[11vw] md:text-[7vw]">The Institution</MaskLine>
          <MaskLine delay={0.4} className="text-[11vw] md:text-[7vw]">
            <span className="text-outline">Behind the Movement.</span>
          </MaskLine>
        </h1>
        <MaskLine delay={0.55}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
            Smart Nagar Nigam is a permanent, non-partisan civic intelligence
            institution transforming ward-level governance across India through
            data-driven accountability, citizen participation, public leadership,
            and technology-enabled urban transformation.
          </p>
        </MaskLine>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="about-vision">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="01" overline="Vision 2030" title="A Global Benchmark for Urban Governance." />
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <blockquote className="border-l-2 border-gold pl-6 font-display text-2xl font-bold leading-snug text-ivory md:pl-8 md:text-3xl">
              "To establish Delhi as a global benchmark for urban governance, where every
              councillor is accountable, every citizen is empowered, and every ward thrives."
            </blockquote>
            <p className="mt-4 pl-6 text-xs uppercase tracking-[0.25em] text-gold md:pl-8">
              Smart Nagar Nigam — Vision 2030
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-base leading-relaxed text-white/70 md:text-lg">
              Built on six years of urban governance research, Smart Nagar Nigam exists
              because the last mile of governance — the ward — remains largely unmeasured,
              unaccountable, and invisible to the very citizens it exists to serve. Macro
              achievements from New Delhi's policy corridors have not always reached the
              street, the drain, the park, or the grievance window at the ward level. This
              institution was built precisely to close that gap.
            </p>
            <p className="mt-6 text-base leading-relaxed text-white/70 md:text-lg">
              Citizens are not a beneficiary category of Smart Nagar Nigam. They are its
              central purpose. Every initiative, every document, every data point, and every
              event exists because 20 million citizens of Delhi deserve governance that is
              measured, transparent, and held accountable.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="section-light border-t border-white/10" data-testid="about-principles">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="02" overline="The Foundation" title="Four Inviolable Principles." />
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.06} className="bg-coal p-8 md:p-12">
              <span className="font-display text-sm font-bold tracking-[0.3em] text-gold">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-ivory md:text-3xl">{p.name}</h3>
              <p className="mt-4 text-base leading-relaxed text-white/70">{p.means}</p>
              <p className="mt-4 text-sm leading-relaxed text-white/50">{p.applied}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="about-architecture">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="03" overline="The Platform's Architecture" title="An Integrated Ecosystem of Initiatives." />
        <p className="mb-12 max-w-3xl text-base leading-relaxed text-white/70 md:text-lg">
          Smart Nagar Nigam operates through an integrated ecosystem of interconnected
          initiatives, each reinforcing the others.
        </p>
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-3">
          {ARCHITECTURE.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.05} className="bg-obsidian p-8 md:p-10">
              <span className="font-display text-sm font-bold tracking-[0.3em] text-gold">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 font-display text-xl font-bold leading-snug text-ivory">{a.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">{a.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="relative overflow-hidden border-t border-white/10" data-testid="about-why">
      <div className="absolute inset-0">
        <img src={IMAGES.architecture} alt="Illuminated skyscrapers at night — the stakes of urban governance" className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-obsidian/85" />
      </div>
      <div className="relative z-10 mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="04" overline="A Closing Word" title="Why This Matters." />
        <div className="max-w-4xl space-y-6 text-base leading-relaxed text-white/75 md:text-lg">
          <Reveal>
            <p>
              In the seventy-five years since Independence, India has built institutions at
              every scale of government — from the Supreme Court to the Gram Sabha. But the
              ward — the most immediate unit of democratic governance, the space where
              citizens interact with the state every single day — has remained the least
              measured, least accountable, and least empowered level of that architecture.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p>
              The 74th Constitutional Amendment gave wards their constitutional status in
              1993. Three decades later, the promise of that Amendment — ward committees that
              function, councillors who are accountable, citizens who are partners — remains
              largely aspirational. Smart Nagar Nigam exists to make it real.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-display text-xl font-bold leading-snug text-ivory md:text-2xl">
              The world's best capital is built ward by ward, citizen by citizen. That
              project begins here.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-wrap items-center gap-8">
            <Link to="/initiatives" data-testid="about-initiatives-cta" className="inline-flex items-center gap-3 bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-obsidian transition-colors duration-300 hover:bg-gold-light">
              Explore the Initiatives <ArrowRight size={16} />
            </Link>
            <ShareRow title="About Smart Nagar Nigam" path="/about" />
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default About;
